import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangleIcon } from 'lucide-react';
import { ViewToggle, type ViewMode } from '../components/ViewToggle';
import { CustomizeFieldsMenu } from '../components/CustomizeFieldsMenu';
import { useVisibleFields } from '../hooks/useVisibleFields';
import { defaultLoanFieldIds, loanFields } from '../data/fieldCatalog';
import { loans, loanStages } from '../data/loanData';
export function Loans() {
  const [view, setView] = useState<ViewMode>('list');
  const navigate = useNavigate();
  const {
    fieldIds,
    toggle,
    move,
    reset
  } = useVisibleFields('uml.loans.fields', defaultLoanFieldIds);
  const columns = fieldIds.map(id => loanFields.find(field => field.id === id)).filter((field): field is (typeof loanFields)[number] => Boolean(field));
  return <div className="space-y-4" data-id="element-888" data-mp="2j88xlfbsg1ai">
      <div className="flex flex-wrap items-end justify-between gap-3" data-id="element-889" data-mp="2o5ikoobpg0v6">
        <div data-id="element-890" data-mp="2o3fv25egs9qz">
          <h1 className="text-[22px] font-bold text-ink" data-id="element-891" data-mp="2o36166qatig0">Loans in process</h1>
          <p className="mt-1 text-[13px] text-gray-500" data-id="element-892" data-mp="1i5ly315booy5">
            {loans.length} active files ·{' '}
            <span className="font-medium text-amber-600" data-id="element-893" data-mp="1e4s8m3c8f235">
              {loans.filter(loan => loan.stalled).length} stalled
            </span>{' '}
            · leads are kept in their own list
          </p>
        </div>
        <div className="flex items-center gap-2" data-id="element-894" data-mp="21sa2q5iyyc9a">
          <CustomizeFieldsMenu fields={loanFields} selectedIds={fieldIds} onToggle={toggle} onMove={move} onReset={reset} anchorLabel="Borrower" data-id="element-895" data-mp="21qyrjkn72285" />
          <ViewToggle value={view} onChange={setView} data-id="element-896" data-mp="1s0ds5uzp4to6" />
        </div>
      </div>

      {view === 'list' ? <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white" data-id="element-897" data-mp="1n1j9qu5n574l">
          <table className="w-full min-w-[720px] text-left" data-id="element-898" data-mp="2kkujiole8qwq">
            <thead className="border-b border-gray-200 bg-gray-50 text-[11px] uppercase tracking-wider text-gray-500" data-id="element-899" data-mp="2fqzynhu1y5w2">
              <tr data-id="element-900" data-mp="dv0uz5r9mvz3">
                <th scope="col" className="px-5 py-2.5 font-semibold" data-id="element-901" data-mp="dwv7pwsdvhs3">
                  Borrower
                </th>
                {columns.map(column => <th key={column.id} scope="col" className="px-5 py-2.5 font-semibold" data-id="element-902" data-mp="43wkai8mhxa2">
                    {column.label}
                  </th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 text-[13px]" data-id="element-903" data-mp="3qb4gatxnkrtf">
              {loans.map(loan => <tr key={loan.id} onClick={() => navigate(`/loans/${loan.id}`)} className="cursor-pointer hover:bg-gray-50" data-id="element-904" data-mp="3vck24o7nn2cv">
                  <td className="px-5 py-3" data-id="element-905" data-mp="3mauc9mtf8bh7">
                    <div className="flex items-center gap-2 font-semibold text-ink" data-id="element-906" data-mp="1fk4dht25zw92">
                      {loan.borrower}
                      {loan.stalled && <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10.5px] font-medium text-amber-700" data-id="element-907" data-mp="1kgv43ablia8q">
                          Stalled {loan.daysInStage}d
                        </span>}
                    </div>
                    <div className="text-[12px] text-gray-500" data-id="element-908" data-mp="23fg27yq57ugg">{loan.state}</div>
                  </td>
                  {columns.map(column => <td key={column.id} className="px-5 py-3 text-gray-600" data-id="element-909" data-mp="1ymd9fst6wp1b">
                      {column.render ? column.render(loan) : column.value(loan)}
                    </td>)}
                </tr>)}
            </tbody>
          </table>
          {columns.length === 0 && <p className="border-t border-gray-100 px-5 py-3 text-[12px] text-gray-500" data-id="element-910" data-mp="2icgxgc7jm7jo">
              No fields selected — use Fields to add columns.
            </p>}
        </div> : <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5" data-id="element-911" data-mp="nyfqf41uae8i">
          {loanStages.map(stage => {
        const items = loans.filter(loan => loan.stage === stage);
        return <section key={stage} className="rounded-xl bg-gray-100/70 p-2.5" data-id="element-912" data-mp="ue6en7ytq4rr">
                <header className="flex items-center justify-between px-1 pb-2" data-id="element-913" data-mp="yg9r9zx8bfem">
                  <h2 className="text-[12px] font-semibold text-ink" data-id="element-914" data-mp="ye6qqlyga3vn">{stage}</h2>
                  <span className="text-[11.5px] text-gray-500" data-id="element-915" data-mp="13eukhfe2cytk">{items.length}</span>
                </header>
                <div className="space-y-2" data-id="element-916" data-mp="6qy70jdxajmk">
                  {items.map(loan => <button key={loan.id} type="button" onClick={() => navigate(`/loans/${loan.id}`)} className="w-full rounded-xl border border-gray-200 bg-white p-3.5 text-left transition-shadow hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-brand/30" data-id="element-917" data-mp="ar8igbkhi28t">
                      <div className="flex items-start gap-2" data-id="element-918" data-mp="3o4jxrnz4cck5">
                        <p className="min-w-0 truncate text-[13px] font-semibold text-ink" data-id="element-919" data-mp="3glu5584j0w7j">
                          {loan.borrower}
                        </p>
                        {loan.stalled && <AlertTriangleIcon className="ml-auto h-3.5 w-3.5 shrink-0 text-amber-500" aria-label="Stalled" data-id="element-920" data-mp="36nmtn4t0b6cx" />}
                      </div>
                      <dl className="mt-2 space-y-1" data-id="element-921" data-mp="2xoj9dxi3skwu">
                        {columns.filter(column => column.id !== 'stage').map(column => <div key={column.id} className="flex items-baseline gap-2" data-id="element-922" data-mp="2nvc56fz3nayc">
                              <dt className="w-24 shrink-0 truncate text-[10.5px] uppercase tracking-wide text-gray-400" data-id="element-923" data-mp="2j0547fx7skd4">
                                {column.label}
                              </dt>
                              <dd className="min-w-0 flex-1 truncate text-[12px] text-ink" data-id="element-924" data-mp="29zqiwgk9359m">
                                {column.value(loan)}
                              </dd>
                            </div>)}
                      </dl>
                    </button>)}
                  {items.length === 0 && <p className="rounded-lg border border-dashed border-gray-300 px-3 py-6 text-center text-[11.5px] text-gray-400" data-id="element-925" data-mp="1va5e25btn14r">
                      Empty
                    </p>}
                </div>
              </section>;
      })}
        </div>}

    </div>;
}